import { useGroupListController } from '@authifi/clique-react-ui';
import type { Group } from '@authifi/clique-react-ui';

interface CustomGroupViewProps {
  onSelect: (group: Group) => void;
}

/**
 * Demonstrates the "headless" customization path: instead of rendering the
 * library's <GroupList> component, this view consumes `useGroupListController`
 * directly and renders a completely bespoke structure (a search box + card
 * wall) while reusing all of the library's data-fetching, search, and
 * pagination logic. The library owns the behaviour; the app owns the markup.
 */
export function CustomGroupView({ onSelect }: CustomGroupViewProps) {
  const { groups, totalCount, search, setSearch, isInitialLoad, isError, refetch } =
    useGroupListController({ pageSize: 50 });

  if (isInitialLoad) {
    return (
      <div className="py-12 text-center" style={{ color: 'var(--clique-fg-secondary)' }}>
        <i aria-hidden="true" className="pi pi-spin pi-spinner text-2xl" />
        <p className="mt-2">Loading groups…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-12 text-center" style={{ color: 'var(--clique-destructive)' }}>
        <i aria-hidden="true" className="pi pi-exclamation-triangle text-2xl" />
        <p className="mt-2">Failed to load groups.</p>
        <button
          type="button"
          onClick={refetch}
          className="mt-3 px-3 py-1.5 rounded text-sm cursor-pointer"
          style={{ backgroundColor: 'var(--clique-primary)', color: 'var(--clique-primary-fg)' }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-4">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter groups…"
          className="w-full max-w-sm rounded-lg px-3 py-2 text-sm"
          style={{
            backgroundColor: 'var(--clique-bg)',
            border: '1px solid var(--clique-border)',
            color: 'var(--clique-fg)',
          }}
        />
        <span className="text-sm whitespace-nowrap" style={{ color: 'var(--clique-fg-muted)' }}>
          {totalCount ?? 0} total
        </span>
      </div>

      {groups && groups.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {groups.map((group) => (
            <button
              key={group.id}
              type="button"
              onClick={() => onSelect(group)}
              className="text-left rounded-xl p-4 transition-shadow hover:shadow-md cursor-pointer"
              style={{
                backgroundColor: 'var(--clique-bg)',
                border: '1px solid var(--clique-border)',
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold" style={{ color: 'var(--clique-fg)' }}>
                  {group.name}
                </span>
                {group.isPrivileged && (
                  <i
                    aria-hidden="true"
                    className="pi pi-star-fill text-xs"
                    style={{ color: 'var(--clique-warning)' }}
                  />
                )}
              </div>
              <p
                className="text-sm mt-1 line-clamp-2"
                style={{ color: 'var(--clique-fg-secondary)' }}
              >
                {group.description || 'No description'}
              </p>
              <span
                className="text-xs mt-2 inline-block"
                style={{ color: 'var(--clique-fg-tertiary)' }}
              >
                #{group.id}
              </span>
            </button>
          ))}
        </div>
      ) : (
        <div className="py-12 text-center" style={{ color: 'var(--clique-fg-muted)' }}>
          No groups match your filter.
        </div>
      )}
    </div>
  );
}
